import {
  ORDER_STATUS_LABELS,
  ORDER_ITEM_STATUS_LABELS,
} from '../constants/internalOrdersConstants';

export const buildUnitsByUuid = (units = []) =>
  units.reduce((acc, unit) => {
    if (unit?.uuid) {
      acc[unit.uuid] = unit.description || '';
    }
    return acc;
  }, {});

export const buildMaterialOptionLabel = (article) => {
  const code = article?.code?.trim() || '—';
  const externalCode = (article?.external_code || article?.externalCode || '').trim() || '—';
  const description = article?.description?.trim() || '—';
  return `${code} — ${externalCode} — ${description}`;
};

export const mapInventoryArticlesToMaterialOptions = (articles = [], unitsByUuid = {}) =>
  articles.map((article) => ({
    value: article.uuid,
    label: buildMaterialOptionLabel(article),
    code: article.code,
    externalCode: article.external_code || '',
    name: article.name,
    unit: unitsByUuid[article.uuid_unit] || '',
    description: article.description || '',
  }));

export const getInternalOrderApiErrorMessage = (
  error,
  fallbackMessage = 'No se pudo completar la operación'
) => {
  const data = error?.response?.data;

  if (!data) {
    return error?.message || fallbackMessage;
  }

  if (typeof data === 'string') {
    return data;
  }

  const candidates = [data.error, data.detail, data.message];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim().replace(/^Error de validación:\s*/i, '');
    }
  }

  return error?.message || fallbackMessage;
};

export const formatOrderStatus = (status) =>
  ORDER_STATUS_LABELS[status] || status || 'Sin estado';

export const formatOrderItemStatus = (status) =>
  ORDER_ITEM_STATUS_LABELS[status] || status || 'Sin estado';

export const formatRequestedAt = (requestedAt) => {
  if (!requestedAt) return '-';
  const date = new Date(requestedAt);
  if (Number.isNaN(date.getTime())) return requestedAt;
  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const filterInternalOrders = (orders, searchTerm) => {
  if (!searchTerm?.trim()) return orders;

  const normalizedTerm = searchTerm.trim().toLowerCase();

  return orders.filter((order) => {
    const searchableValues = [
      order?.order_number,
      order?.number,
      order?.warehouse_label,
      order?.project_label,
      order?.status,
      order?.status_label,
      order?.observations,
      order?.requested_at_label,
    ];

    return searchableValues.some((value) =>
      String(value ?? '')
        .toLowerCase()
        .includes(normalizedTerm)
    );
  });
};

export const getWarehouseLabelFromRecord = (warehouse) => {
  if (!warehouse) return '';
  return warehouse.name || warehouse.code || '';
};

const buildNaturalSortParts = (value) => {
  const text = String(value ?? '').trim().toLowerCase();
  if (!text) return [];

  return text
    .split(/(\d+)/)
    .filter(Boolean)
    .map((part) => (/^\d+$/.test(part) ? Number(part) : part));
};

const compareNaturalValues = (left, right) => {
  const leftParts = buildNaturalSortParts(left);
  const rightParts = buildNaturalSortParts(right);
  const maxLength = Math.max(leftParts.length, rightParts.length);

  for (let index = 0; index < maxLength; index += 1) {
    const leftPart = leftParts[index] ?? '';
    const rightPart = rightParts[index] ?? '';

    if (leftPart === rightPart) continue;

    if (typeof leftPart === 'number' && typeof rightPart === 'number') {
      return leftPart - rightPart;
    }

    return String(leftPart).localeCompare(String(rightPart), 'es', { numeric: true });
  }

  return 0;
};

export const getProjectOptionLabel = (project) => {
  if (!project) return '';

  const allotmentName = (project.allotment_name || '').trim();
  const blockNumber = (project.block_number || '').trim();
  const lotNumber = (project.lot_number || '').trim();
  const lotLabel = `${blockNumber} ${lotNumber}`.trim();

  if (allotmentName && lotLabel) {
    return `${allotmentName} - ${lotLabel}`;
  }

  if (allotmentName) {
    return allotmentName;
  }

  return (project.name || project.description || '').trim();
};

export const getProjectLabelFromRecord = (project) => getProjectOptionLabel(project);

export const sortProjectOptions = (projects = []) =>
  [...projects].sort((left, right) => {
    const allotmentCompare = (left?.allotment_name || getProjectOptionLabel(left)).localeCompare(
      right?.allotment_name || getProjectOptionLabel(right),
      'es',
      { sensitivity: 'base' }
    );

    if (allotmentCompare !== 0) return allotmentCompare;

    const blockCompare = compareNaturalValues(left?.block_number, right?.block_number);
    if (blockCompare !== 0) return blockCompare;

    return compareNaturalValues(left?.lot_number, right?.lot_number);
  });

export const getWarehouseLabel = (warehouses, uuid) => {
  if (!uuid) return '';
  const warehouse = warehouses.find((item) => item.uuid === uuid);
  if (!warehouse) return '';
  return getWarehouseLabelFromRecord(warehouse);
};

export const getProjectLabel = (projects, uuid) => {
  if (!uuid) return '';
  const project = projects.find((item) => item.uuid === uuid);
  if (!project) return '';
  return getProjectLabelFromRecord(project);
};

export const getArticleLabel = (articlesByUuid, uuid) => {
  if (!uuid) return 'Artículo sin nombre';
  const article = articlesByUuid[uuid];
  if (!article) return uuid;
  return article.external_code
    ? `${article.external_code} — ${article.name}`
    : `${article.code} — ${article.name}`;
};

export const buildArticlesByUuid = (articles = []) =>
  articles.reduce((acc, article) => {
    if (article?.uuid) {
      acc[article.uuid] = article;
    }
    return acc;
  }, {});

export const formatNeededAt = (date, time) => {
  if (!date) return null;
  return time ? `${date}T${time}:00` : `${date}T00:00:00`;
};

export const parseRequestedAt = (requestedAt) => {
  if (!requestedAt) {
    return { needed_date: '', needed_time: '' };
  }

  const date = new Date(requestedAt);
  if (Number.isNaN(date.getTime())) {
    const [datePart, timePart] = String(requestedAt).split('T');
    return {
      needed_date: datePart || '',
      needed_time: timePart ? timePart.slice(0, 5) : '',
    };
  }

  const needed_date = date.toISOString().split('T')[0];
  const needed_time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  return { needed_date, needed_time };
};

export const mapExternalOrderToDisplay = (order, warehouses = [], projects = [], references = {}) => {
  const { warehouse, project } = references;

  return {
    ...order,
    number: order.order_number || order.uuid?.slice(0, 8) || '-',
    warehouse_label: order.warehouse_label || order.warehouse_name || (warehouse
      ? getWarehouseLabelFromRecord(warehouse)
      : getWarehouseLabel(warehouses, order.uuid_destination_warehouse)),
    project_label: order.project_label || order.project_name || (project
      ? getProjectLabelFromRecord(project)
      : getProjectLabel(projects, order.uuid_project)),
    status_label: formatOrderStatus(order.status),
    requested_at_label: formatRequestedAt(order.requested_at || order.created_at),
  };
};

export const mapExternalOrdersToDisplay = (orders = [], warehouses = [], projects = []) =>
  orders.map((order) => mapExternalOrderToDisplay(order, warehouses, projects));

export const buildCreateOrderPayload = (selectedOrder) => {
  const { needed_date, needed_time, materials = [] } = selectedOrder;

  if (!needed_date || !selectedOrder?.uuid_destination_warehouse || !selectedOrder?.uuid_project) {
    return null;
  }

  const validMaterials = materials.filter(
    (item) => item?.materialId && Number(item.quantity) > 0
  );

  if (validMaterials.length === 0) {
    return null;
  }

  return {
    requested_at: formatNeededAt(needed_date, needed_time),
    uuid_destination_warehouse: selectedOrder.uuid_destination_warehouse,
    uuid_project: selectedOrder.uuid_project,
    observations: selectedOrder.observations?.trim() || null,
    items: validMaterials.map((item) => ({
      uuid_inventory_article: item.materialId,
      quantity: Number(item.quantity),
      observations: item.observations || null,
    })),
  };
};

export const getProcessResultMessage = (result) => {
  if (!result) return '';

  const orderStatus = result.external_order?.status;
  const hasReservation = Boolean(result.reservation);
  const missingItems = result.missing_items || [];

  if (hasReservation && orderStatus === 'stock_available') {
    return 'El pedido tiene stock completo y se generó la reserva.';
  }

  if (hasReservation && orderStatus === 'awaiting_transfer') {
    return 'Se reservó el stock disponible. El pedido quedó con faltantes pendientes de abastecimiento.';
  }

  if (orderStatus === 'awaiting_transfer') {
    return 'El pedido tiene faltantes de stock. Podés generar una orden a proveedor.';
  }

  if (missingItems.length > 0) {
    return `El pedido tiene ${missingItems.length} artículo(s) con faltante de stock.`;
  }

  if (orderStatus === 'ready_to_consume') {
    return 'El pedido quedó listo para consumir.';
  }

  return 'Operación completada correctamente.';
};
